import React, { useState, useEffect, useContext } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import * as Sentry from '@sentry/react'

import { FlashMessage, LoadingCircle } from '../../../components/atoms'
import { CreatorLoginTpl } from '../../../components/templates'
import { Meta } from '../../../Meta'

import { validateEmail, validatePassword } from '../../../lib/Validate'

import { CreatorTokenContext } from '../../../context'

import { FlashMessageType } from '../../../types/myTypes'
import { useLoginCreatorMutation } from '../../../types/graphql'

export const CreatorLogin: React.FC = () => {
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [redirect, setRedirect] = useState<boolean>(false)
  const [redirectFrom, setRedirectFrom] = useState<string>('')
  const [flashMessage, setFlashMessage] = useState<FlashMessageType | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const history = useHistory()
  const location = useLocation<{ redirect: boolean; redirectFrom: string }>()

  const { updateCreatorToken } = useContext(CreatorTokenContext)

  const [, setCookie] = useCookies(['token', 'creatorToken'])

  const [loginCreatorMutation] = useLoginCreatorMutation({
    onCompleted: (data) => {
      const token = data?.LoginCreator?.token ?? ''
      updateCreatorToken(token)
      setCookie('creatorToken', token, { path: '/' })
      if (!!redirectFrom) {
        history.push(redirectFrom)
      } else {
        history.push('/creator/mypage')
      }
    },
    onError: (e) => {
      setLoading(false)
      Sentry.captureException(e)
      if (e.message) {
        setFlashMessage({ type: 'inputError', message: e.message })
      } else {
        setFlashMessage({ type: 'systemError', message: 'ログインできませんでした' })
      }
    },
  })

  const onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(event.target.value)
    setEmailError(null)
    const emailError: string | null = validateEmail(event.target.value)
    if (emailError) {
      setEmailError(emailError)
    }
  }
  const onChangePassword = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setPassword(event.target.value)
    setPasswordError(null)
    const passwordError: string | null = validatePassword(event.target.value)
    if (passwordError) {
      setPasswordError(passwordError)
    }
  }

  const login = async (): Promise<void> => {
    setLoading(true)
    setFlashMessage(null)
    await loginCreatorMutation({
      variables: {
        input: {
          email,
          password,
        },
      },
    })
  }

  useEffect(() => {
    if (location.state) {
      setRedirect(location.state.redirect)
      setRedirectFrom(location.state.redirectFrom)
    }
  }, [location.state])

  // location.state.redirect = trueのままだとリロード時に毎回フラッシュメッセージが表示されるのでreplace
  useEffect(() => {
    if (location.state && location.state.redirect === true) {
      history.replace({ ...location, state: undefined })
    }
  }, [history, location])

  return (
    <>
      <Meta title="ログイン" />
      {loading && <LoadingCircle />}
      {flashMessage && <FlashMessage flashMessage={flashMessage} />}
      {redirect && <FlashMessage flashMessage={{ message: '再度ログインしてください', type: 'systemError' }} />}
      <CreatorLoginTpl
        email={email}
        password={password}
        onChangeEmail={onChangeEmail}
        onChangePassword={onChangePassword}
        login={login}
        emailError={emailError}
        passwordError={passwordError}
      />
    </>
  )
}
